import { useState } from "react";
import { motion } from "framer-motion";
import { Cpu, LineChart, Bot, BarChart3, HeadphonesIcon, Target } from "lucide-react";
import AI_SOLUTIONS from "@/data/aiSolutionsData";
import DetailsModal from "@/components/landing/DetailsModal";

const DASHBOARD =
  "https://static.prod-images.emergentagent.com/jobs/220ed046-f4a9-49c2-94d5-bdf9747703f3/images/82405bd087738184ae1abe93801f8e8fa71a35ec011a70d521ebe787607f455c.png";

const ICONS = {
  "ai-lead-qualification": Target,
  "predictive-analytics": LineChart,
  "ai-sales-funnels": Bot,
  "automated-reporting": BarChart3,
  "ai-customer-support": HeadphonesIcon,
  "intelligent-ad-optimization": Cpu,
};

export default function AISolutions() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="ai-solutions"
      data-testid="ai-solutions-section"
      className="relative py-14 md:py-32 overflow-hidden border-t border-white/5 bg-[#070709]"
    >
      <div className="aurora" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        {/* Left: Copy + feature list */}
        <div>
          <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
            / AI Operating System
          </div>
          <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05] max-w-xl">
            An <span className="text-gradient-neon">intelligent layer</span> on top of every growth channel.
          </h2>
          <p className="mt-5 text-zinc-400 max-w-lg text-base leading-relaxed">
            Quantum Star Marketing deploys proprietary AI systems that learn your funnel,
            optimize spend, and unlock revenue you didn't know existed. Click any system
            to see how it deploys, what it ships, and the calibrated outcomes we measure.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {AI_SOLUTIONS.map((f, i) => {
              const Icon = ICONS[f.key] || Target;
              return (
                <motion.button
                  type="button"
                  key={f.key}
                  onClick={() => setActive(f)}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="group rounded-xl glass p-4 hover:bg-white/[0.04] transition-colors text-left ring-glow"
                  data-testid={`ai-feature-${i}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} className="text-cyan-300" />
                    <span className="font-clash text-sm font-medium">{f.name}</span>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{f.tagline}</p>
                  <div className="mt-3 flex items-center text-[11px] text-zinc-500 group-hover:text-cyan-300 transition-colors">
                    See deployment
                    <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Right: Dashboard mock with floating cards */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl overflow-hidden glass-strong glow-purple"
          >
            <img
              src={DASHBOARD}
              alt="AI analytics dashboard mockup"
              className="w-full h-auto block"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/40 to-transparent" />
          </motion.div>

          {/* Floating card 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="absolute -top-4 -left-4 md:-left-10 glass-strong rounded-2xl p-4 w-[200px] float-y"
          >
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.2em] text-zinc-500">
              Live ROAS
            </div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-clash text-3xl font-semibold text-white">6.4x</span>
              <span className="text-xs text-emerald-400">▲ 28%</span>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
              <div className="h-full w-3/4 bg-gradient-to-r from-cyan-400 to-purple-500" />
            </div>
          </motion.div>

          {/* Floating card 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="absolute -bottom-6 -right-4 md:-right-8 glass-strong rounded-2xl p-4 w-[220px] float-y"
            style={{ animationDelay: "1.5s" }}
          >
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.2em] text-zinc-500">
              AI Predictions
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-zinc-300">
              <span>Q4 Revenue</span>
              <span className="text-cyan-300 font-mono-acc">AED 6.2M</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-zinc-300">
              <span>LTV (90d)</span>
              <span className="text-purple-300 font-mono-acc">AED 1,840</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-zinc-300">
              <span>Churn risk</span>
              <span className="text-emerald-400 font-mono-acc">3.2%</span>
            </div>
          </motion.div>
        </div>
      </div>

      {active && (
        <DetailsModal
          item={active}
          onClose={() => setActive(null)}
          testIdPrefix="ai-solution-details"
        />
      )}
    </section>
  );
}
